import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, Plus, X, AlertTriangle, Ban, User } from 'lucide-react';
import {
  atlasApi, AtlasUser, OnboardingExecucao, OnboardingTrilha,
} from '../shared/atlasApi';
import { inputCls, labelCls } from '../shared/ListaEditavel';
import FeedbackBanner, { Feedback } from '../shared/FeedbackBanner';
import EtapasOnboarding from './EtapasOnboarding';

interface Props {
  user: AtlasUser;
  podeEditar: boolean;
}

function fdata(s?: string | null) {
  if (!s) return '—';
  try { return new Date(s).toLocaleDateString('pt-BR'); } catch { return s; }
}

type ColunaId = 'em_andamento' | 'atrasado' | 'concluido';
const COLUNAS: { id: ColunaId; titulo: string; cor: string }[] = [
  { id: 'em_andamento', titulo: 'Em andamento', cor: 'bg-blue-500' },
  { id: 'atrasado', titulo: 'Atrasado', cor: 'bg-rose-500' },
  { id: 'concluido', titulo: 'Concluído', cor: 'bg-emerald-500' },
];

function colunaDe(e: OnboardingExecucao): ColunaId | null {
  if (e.status === 'concluido') return 'concluido';
  if (e.status === 'em_andamento') return e.atrasada ? 'atrasado' : 'em_andamento';
  return null; // cancelado fica oculto
}

const CardExec: React.FC<{ exec: OnboardingExecucao; onClick: () => void }> = ({ exec, onClick }) => (
  <button onClick={onClick} className="text-left w-full bg-white rounded-xl border border-gray-200 p-4 hover:border-purple-300 hover:shadow-md transition">
    <div className="flex items-start gap-2">
      <div className="w-8 h-8 rounded-lg bg-purple-50 text-purple-600 flex items-center justify-center flex-shrink-0"><User className="w-4 h-4" /></div>
      <div className="min-w-0 flex-1">
        <div className="font-medium text-gray-900 truncate">{exec.colaborador_nome || `#${exec.funccodi}`}</div>
        <div className="text-xs text-gray-400 truncate">{exec.trilha_nome}</div>
      </div>
      {exec.atrasada && exec.status === 'em_andamento' && <AlertTriangle className="w-4 h-4 text-rose-500 flex-shrink-0" />}
    </div>
    <div className="mt-3">
      <div className="flex justify-between text-[11px] text-gray-500 mb-1">
        <span>Progresso</span>
        <span>{exec.progresso_pct}%</span>
      </div>
      <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
        <div className={`h-full ${exec.status === 'concluido' ? 'bg-emerald-500' : exec.atrasada ? 'bg-rose-500' : 'bg-purple-500'}`} style={{ width: `${exec.progresso_pct}%` }} />
      </div>
    </div>
    <div className="text-[11px] text-gray-400 mt-2">Início: {fdata(exec.data_inicio)}</div>
  </button>
);

const Execucoes: React.FC<Props> = ({ user, podeEditar }) => {
  const [itens, setItens] = useState<OnboardingExecucao[]>([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState<string | null>(null);

  const [sel, setSel] = useState<OnboardingExecucao | null>(null);
  const [iniciarAberto, setIniciarAberto] = useState(false);

  const carregar = useCallback(() => {
    setLoading(true);
    setErro(null);
    atlasApi.getOnboardingExecucoes(user)
      .then(r => setItens(r.itens || []))
      .catch(e => setErro(e.message || 'Erro ao carregar'))
      .finally(() => setLoading(false));
  }, [user]);

  useEffect(() => { carregar(); }, [carregar]);

  const cancelar = async (exec: OnboardingExecucao) => {
    if (!window.confirm(`Cancelar o onboarding de ${exec.colaborador_nome || `#${exec.funccodi}`}?`)) return;
    try {
      await atlasApi.cancelarOnboardingExecucao(user, exec._id);
      setSel(null);
      carregar();
    } catch (e: any) {
      setErro(e.message || 'Erro ao cancelar.');
    }
  };

  const porColuna = (id: ColunaId) => itens.filter(e => colunaDe(e) === id);

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <h2 className="text-lg font-bold text-gray-900 flex-1">Execuções de onboarding</h2>
        {podeEditar && (
          <button onClick={() => setIniciarAberto(true)} className="px-4 py-2 rounded-lg bg-purple-600 hover:bg-purple-700 text-white text-sm font-medium flex items-center gap-2">
            <Plus className="w-4 h-4" /> Iniciar onboarding
          </button>
        )}
      </div>

      {erro && <div className="bg-rose-50 border border-rose-200 rounded-lg px-3 py-2 text-sm text-rose-700">{erro}</div>}

      {loading ? (
        <div className="p-12 text-center text-gray-400"><Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" /> Carregando...</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {COLUNAS.map(col => {
            const lista = porColuna(col.id);
            return (
              <div key={col.id} className="space-y-3">
                <div className="flex items-center gap-2">
                  <span className={`w-2.5 h-2.5 rounded-full ${col.cor}`} />
                  <h3 className="text-sm font-bold text-gray-700">{col.titulo}</h3>
                  <span className="text-xs text-gray-400">{lista.length}</span>
                </div>
                {lista.length === 0 ? (
                  <div className="rounded-xl border border-dashed border-gray-200 p-6 text-center text-xs text-gray-400">Vazio</div>
                ) : (
                  lista.map(exec => <CardExec key={exec._id} exec={exec} onClick={() => setSel(exec)} />)
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Painel de detalhe (etapas) */}
      {sel && (
        <div className="fixed inset-0 z-[10050] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setSel(null)}>
          <div className="bg-white max-w-lg w-full max-h-[90vh] overflow-y-auto rounded-2xl shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="flex items-center gap-3 p-5 border-b border-gray-100 sticky top-0 bg-white z-10">
              <div className="flex-1 min-w-0">
                <h3 className="font-bold text-gray-900 truncate">{sel.colaborador_nome || `#${sel.funccodi}`}</h3>
                <p className="text-xs text-gray-400 truncate">{sel.trilha_nome} · início {fdata(sel.data_inicio)}</p>
              </div>
              <button onClick={() => setSel(null)} className="p-2 rounded-lg text-gray-400 hover:bg-gray-100"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <div className="flex justify-between text-xs text-gray-500 mb-1">
                  <span>Progresso</span>
                  <span>{sel.progresso_pct}%</span>
                </div>
                <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                  <div className={`h-full ${sel.status === 'concluido' ? 'bg-emerald-500' : sel.atrasada ? 'bg-rose-500' : 'bg-purple-500'}`} style={{ width: `${sel.progresso_pct}%` }} />
                </div>
              </div>

              <EtapasOnboarding
                user={user}
                execId={sel._id}
                etapas={sel.etapas}
                podeEditar={podeEditar && sel.status === 'em_andamento'}
                onEtapaAlterada={({ progresso_pct, statusExec }) => {
                  setSel(s => s ? { ...s, progresso_pct, status: statusExec as OnboardingExecucao['status'] } : s);
                  // Atualiza a lista (progresso/status) sem refetch completo.
                  setItens(prev => prev.map(x => x._id === sel._id ? { ...x, progresso_pct, status: statusExec as OnboardingExecucao['status'] } : x));
                }}
              />

              {podeEditar && sel.status === 'em_andamento' && (
                <div className="flex justify-end pt-2 border-t border-gray-100">
                  <button onClick={() => cancelar(sel)} className="px-4 py-2 rounded-lg bg-rose-50 text-rose-700 hover:bg-rose-100 text-sm font-medium flex items-center gap-2">
                    <Ban className="w-4 h-4" /> Cancelar onboarding
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {iniciarAberto && (
        <ModalIniciar user={user} onClose={() => setIniciarAberto(false)} onCriado={() => { setIniciarAberto(false); carregar(); }} />
      )}
    </div>
  );
};

// ============================================================
// Modal: iniciar onboarding (funccodi + trilha)
// ============================================================
const ModalIniciar: React.FC<{ user: AtlasUser; onClose: () => void; onCriado: () => void }> = ({ user, onClose, onCriado }) => {
  const [trilhas, setTrilhas] = useState<OnboardingTrilha[]>([]);
  const [funccodi, setFunccodi] = useState('');
  const [trilhaId, setTrilhaId] = useState('');
  const [salvando, setSalvando] = useState(false);
  const [fb, setFb] = useState<Feedback>(null);

  useEffect(() => {
    atlasApi.getOnboardingTrilhas(user)
      .then(r => {
        const ativas = (r.itens || []).filter(t => t.ativo !== false);
        setTrilhas(ativas);
        if (ativas[0]) setTrilhaId(ativas[0]._id);
      })
      .catch(() => {});
  }, [user]);

  const criar = async () => {
    const fc = parseInt(funccodi, 10);
    if (!fc || isNaN(fc)) { setFb({ tipo: 'erro', msg: 'Informe um funccodi válido.' }); return; }
    if (!trilhaId) { setFb({ tipo: 'erro', msg: 'Selecione uma trilha.' }); return; }
    setSalvando(true);
    setFb(null);
    try {
      await atlasApi.criarOnboardingExecucao(user, fc, trilhaId);
      onCriado();
    } catch (e: any) {
      if (e.status === 409) setFb({ tipo: 'erro', msg: 'Já existe um onboarding para este colaborador nesta trilha.' });
      else setFb({ tipo: 'erro', msg: e.message || 'Erro ao iniciar.' });
      setSalvando(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[10051] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white max-w-md w-full rounded-2xl shadow-2xl" onClick={e => e.stopPropagation()}>
        <div className="flex items-center gap-3 p-5 border-b border-gray-100">
          <h3 className="font-bold text-gray-900 flex-1">Iniciar onboarding</h3>
          <button onClick={onClose} className="p-2 rounded-lg text-gray-400 hover:bg-gray-100"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 space-y-3">
          <div>
            <label className={labelCls}>Colaborador (funccodi) *</label>
            <input className={inputCls} type="number" value={funccodi} onChange={e => setFunccodi(e.target.value)} placeholder="12534" />
          </div>
          <div>
            <label className={labelCls}>Trilha *</label>
            <select className={inputCls} value={trilhaId} onChange={e => setTrilhaId(e.target.value)}>
              {trilhas.length === 0 && <option value="">Nenhuma trilha ativa</option>}
              {trilhas.map(t => <option key={t._id} value={t._id}>{t.nome}</option>)}
            </select>
          </div>

          <FeedbackBanner feedback={fb} />

          <div className="flex justify-end gap-2 pt-1">
            <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">Cancelar</button>
            <button onClick={criar} disabled={salvando} className="inline-flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 disabled:opacity-50 text-white text-sm font-medium rounded-lg">
              {salvando ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Iniciar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Execucoes;
